import { useDiagramData } from '@/hooks/useDiagramData';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, GitBranch, Map, Shield, Lock, Tag, Radio, Globe, BarChart3 } from 'lucide-react';
import ReviewCommentsCallout from '@/components/review/ReviewCommentsCallout';
import { entityTypeLabels } from '@/lib/constants';

export default function OverviewTab() {
  const {
    currentDiagram,
    stats,
    pathsWithoutAuth,
    pathsWithoutName,
    protocols,
    mostConnectedEntity,
    entityTypeDistribution,
  } = useDiagramData();
  const reviewIndex = useReviewCommentsIndex();
  
  if (!stats) {
    return <div>No diagram data available</div>;
  }

  const typeBreakdown = Object.entries(entityTypeDistribution)
    .filter(([_, count]) => Number(count) > 0)
    .map(([type, count]) => {
      const label = entityTypeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1);
      return `${count} ${label}${count !== 1 ? 's' : ''}`;
    })
    .join(', ');

  const diagramComments =
    typeof currentDiagram?.id === 'number'
      ? reviewIndex.getDiagramComments(currentDiagram.id)
      : [];

  return (
    <div className="space-y-6">
      <ReviewCommentsCallout comments={diagramComments} />
      {/* Statistics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Entities</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.entityCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Connections</CardTitle>
            <GitBranch className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pathCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Boundaries</CardTitle>
            <Map className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueBoundaries}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Protocols Used</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueProtocols}</div>
          </CardContent>
        </Card>
      </div>

      {/* Important Observations */}
      <Card>
        <CardHeader>
          <CardTitle>Analysis Findings</CardTitle>
          <CardDescription>Key observations from the diagram</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Lock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <div className="text-sm font-medium">Authentication Coverage</div>
              <div className={`text-sm ${pathsWithoutAuth > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {pathsWithoutAuth} of {stats.pathCount} connections have no authentication information specified
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Tag className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <div className="text-sm font-medium">Connection Labels</div>
              <div className={`text-sm ${pathsWithoutName > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {pathsWithoutName} connection{pathsWithoutName !== 1 ? 's' : ''} have no descriptive labels
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Radio className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <div className="text-sm font-medium">Protocol Distribution</div>
              <div className="text-sm text-muted-foreground">
                {protocols.length === 1
                  ? `All connections use ${protocols[0]}`
                  : `Multiple protocols in use: ${protocols.join(', ')}`}
              </div>
            </div>
          </div>

          {mostConnectedEntity && (
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <Globe className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <div className="text-sm font-medium">Most Connected Entity</div>
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium">{mostConnectedEntity.entity?.name}</span> with{' '}
                  {mostConnectedEntity.count} connection{mostConnectedEntity.count !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 rounded-lg border p-3">
            <BarChart3 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <div className="text-sm font-medium">Entity Distribution</div>
              <div className="text-sm text-muted-foreground">{typeBreakdown}</div>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
